/**
 * @fileoverview Premium Skeleton Component for Skillzy.pro
 *
 * Loading placeholder with shimmer animation. Supports various shapes
 * and can compose together to build complex loading states.
 *
 * @example
 * // Simple text skeleton
 * <Skeleton width="60%" height={16} />
 *
 * // Avatar skeleton
 * <Skeleton variant="circle" width={48} height={48} />
 *
 * // Card skeleton composition
 * <Skeleton.Card />
 *
 * // Custom composition
 * <Skeleton.Group>
 *   <Skeleton variant="circle" width={40} height={40} />
 *   <div>
 *     <Skeleton width="70%" height={14} />
 *     <Skeleton width="40%" height={12} className="mt-2" />
 *   </div>
 * </Skeleton.Group>
 */

import React, { forwardRef } from 'react';
import clsx from 'clsx';

/** @type {Record<string, string>} */
const variantClasses = {
  text: 'rounded-md',
  circle: 'rounded-full',
  rect: 'rounded-lg',
  rounded: 'rounded-xl',
};

/**
 * Resolves a dimension value to a CSS-compatible string.
 * Numbers become px values, strings are passed through.
 * @param {number | string | undefined} value
 * @returns {string | undefined}
 */
function resolveDimension(value) {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return `${value}px`;
  return value;
}

/**
 * Premium Skeleton component with shimmer animation.
 *
 * @param {Object} props
 * @param {'text' | 'circle' | 'rect' | 'rounded'} [props.variant='text'] - Shape variant
 * @param {number | string} [props.width] - Width (number = px, string = any CSS value)
 * @param {number | string} [props.height=16] - Height
 * @param {boolean} [props.animate=true] - Enable shimmer animation
 * @param {number} [props.lines] - Render multiple skeleton lines
 * @param {string} [props.className]
 */
const Skeleton = forwardRef(({
  variant = 'text',
  width,
  height = 16,
  animate = true,
  lines,
  className,
  ...rest
}, ref) => {
  // Multi-line mode
  if (lines && lines > 1) {
    return (
      <div ref={ref} className={clsx('space-y-2.5', className)} role="status" aria-label="Loading">
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={i}
            className={clsx(
              'bg-slate-200 dark:bg-slate-800',
              animate && 'ds-animate-shimmer',
              variantClasses[variant] || variantClasses.text
            )}
            style={{
              width: i === lines - 1 ? '75%' : resolveDimension(width) || '100%',
              height: resolveDimension(height),
            }}
          />
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role="status"
      aria-label="Loading"
      className={clsx(
        'bg-slate-200 dark:bg-slate-800',
        animate && 'ds-animate-shimmer',
        variantClasses[variant] || variantClasses.text,
        className
      )}
      style={{
        width: resolveDimension(width),
        height: resolveDimension(height),
      }}
      {...rest}
    >
      <span className="sr-only">Loading…</span>
    </div>
  );
});

Skeleton.displayName = 'Skeleton';

/**
 * Skeleton.Group - Flex container for composing skeleton layouts.
 */
const SkeletonGroup = forwardRef(({
  className,
  children,
  direction = 'row',
  gap = 3,
  align = 'center',
  ...rest
}, ref) => (
  <div
    ref={ref}
    className={clsx(
      'flex',
      direction === 'row' ? 'flex-row' : 'flex-col',
      `gap-${gap}`,
      align === 'center' && 'items-center',
      align === 'start' && 'items-start',
      className
    )}
    role="status"
    aria-label="Loading"
    {...rest}
  >
    {children}
  </div>
));

SkeletonGroup.displayName = 'SkeletonGroup';

/**
 * Skeleton.Card - Pre-built card loading skeleton.
 * @param {Object} props
 * @param {boolean} [props.showImage=false] - Show image placeholder
 * @param {boolean} [props.showAvatar=true] - Show avatar in header
 * @param {number} [props.lines=3] - Number of text lines
 */
const SkeletonCard = forwardRef(({
  showImage = false,
  showAvatar = true,
  lines = 3,
  className,
  ...rest
}, ref) => (
  <div
    ref={ref}
    className={clsx(
      'rounded-xl border border-slate-200 bg-white p-5',
      'dark:border-slate-800 dark:bg-slate-900',
      'animate-pulse',
      className
    )}
    role="status"
    aria-label="Loading card"
    {...rest}
  >
    {/* Image placeholder */}
    {showImage && (
      <div className="mb-4 h-40 w-full rounded-lg bg-slate-200 dark:bg-slate-800 ds-animate-shimmer" />
    )}

    {/* Header with avatar */}
    {showAvatar && (
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-slate-200 dark:bg-slate-800 ds-animate-shimmer" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-3/5 rounded-md bg-slate-200 dark:bg-slate-800 ds-animate-shimmer" />
          <div className="h-3 w-2/5 rounded-md bg-slate-200 dark:bg-slate-800 ds-animate-shimmer" />
        </div>
      </div>
    )}

    {/* Text lines */}
    <div className="space-y-2.5">
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="h-3 rounded-md bg-slate-200 dark:bg-slate-800 ds-animate-shimmer"
          style={{ width: i === lines - 1 ? '60%' : i === 0 ? '90%' : '100%' }}
        />
      ))}
    </div>

    <span className="sr-only">Loading…</span>
  </div>
));

SkeletonCard.displayName = 'SkeletonCard';

/**
 * Skeleton.Table - Pre-built table loading skeleton.
 * @param {Object} props
 * @param {number} [props.rows=5] - Number of rows
 * @param {number} [props.cols=4] - Number of columns
 */
const SkeletonTable = forwardRef(({ rows = 5, cols = 4, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={clsx(
      'rounded-xl border border-slate-200 bg-white overflow-hidden',
      'dark:border-slate-800 dark:bg-slate-900',
      className
    )}
    role="status"
    aria-label="Loading table"
    {...rest}
  >
    {/* Header row */}
    <div className="flex gap-4 border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
      {Array.from({ length: cols }, (_, i) => (
        <div key={i} className="h-3 flex-1 rounded bg-slate-200 dark:bg-slate-700 ds-animate-shimmer" />
      ))}
    </div>

    {/* Data rows */}
    {Array.from({ length: rows }, (_, rowIdx) => (
      <div
        key={rowIdx}
        className={clsx(
          'flex gap-4 p-4',
          rowIdx !== rows - 1 && 'border-b border-slate-100 dark:border-slate-800'
        )}
      >
        {Array.from({ length: cols }, (_, colIdx) => (
          <div
            key={colIdx}
            className="h-3 flex-1 rounded bg-slate-200 dark:bg-slate-800 ds-animate-shimmer"
            style={{ width: colIdx === 0 ? '80%' : '100%' }}
          />
        ))}
      </div>
    ))}

    <span className="sr-only">Loading…</span>
  </div>
));

SkeletonTable.displayName = 'SkeletonTable';

// Attach subcomponents
Skeleton.Group = SkeletonGroup;
Skeleton.Card = SkeletonCard;
Skeleton.Table = SkeletonTable;

export default Skeleton;
